import { hc } from 'hono/client'
// Type-only, and it must stay type-only: this is the one place apps/web reaches into
// apps/api. TypeScript erases the import before the bundler sees it, so no server code
// can reach the browser. There is deliberately no workspace dependency on the api --
// adding one would pull its whole tree into the web install and let someone import a
// runtime value by accident.
import type { AppType } from '../../../api/src/app.ts'
import { defineMessages, getLang, messagesNow } from '../i18n/index.ts'
import { reportProblem } from '../lib/telemetry.ts'
import { peekVisit } from '../lib/visitId.ts'

/** Empty in dev: the edge serves the app and the api on one origin. */
const API_URL = import.meta.env.VITE_API_URL ?? ''

/**
 * Route shapes come from the api's own AppType, so a renamed route or a changed body is a
 * compile error here rather than a 400 in production. The session is an httpOnly cookie,
 * which means there is no token for this layer to manage -- only `credentials`.
 */
export const api = hc<AppType>(API_URL, {
	init: { credentials: 'include' },
	// Her interface language, read per request so a switch applies to the next call: the api
	// answers its messages, and writes its suggestions, in it. The visit, once a teacher has one, lets
	// the events the server writes for a request say which visit they belong to.
	headers: () => {
		const visit = peekVisit()
		return { 'accept-language': getLang(), ...(visit && { 'x-tt-visit': visit }) }
	},
}).api

export const mediaUrl = (path: string) => `${API_URL}${path}`

let absolute: typeof api | undefined
/**
 * The same routes as `api`, but `$url()` works: it needs an absolute base, and `API_URL` is empty
 * when the edge serves both. For `navigator.sendBeacon`, which takes a URL and no hc call, so the
 * address still comes from the route table and never from a hand-written path.
 */
export const apiUrl = () => {
	absolute ??= hc<AppType>(API_URL || location.origin).api
	return absolute
}

const messages = defineMessages({
	en: {
		noAnswer: 'The server did not answer. Try again in a moment.',
		aiFailed:
			'The AI could not finish this. Try again in a few minutes; a shorter text works more often.',
		noConnection: 'No connection. Check your internet and try again.',
		failed: (status: number) => `Request failed (${status})`,
		internal: 'Something went wrong on our side. Nothing you typed is lost.',
		generic: 'Something went wrong. Please try again.',
	},
	ro: {
		noAnswer: 'Serverul nu a răspuns. Încearcă din nou peste câteva clipe.',
		aiFailed:
			'AI-ul nu a reușit să termine. Încearcă din nou peste câteva minute; un text mai scurt merge mai des.',
		noConnection: 'Fără conexiune. Verifică internetul și încearcă din nou.',
		failed: (status: number) => `Cererea nu a reușit (${status})`,
		internal: 'Ceva nu a mers bine la noi. Nimic din ce ai scris nu s-a pierdut.',
		generic: 'Ceva nu a mers bine. Te rugăm să încerci din nou.',
	},
	ru: {
		noAnswer: 'Сервер не ответил. Попробуйте ещё раз через минуту.',
		aiFailed:
			'ИИ не смог это закончить. Попробуйте через несколько минут; с текстом покороче получается чаще.',
		noConnection: 'Нет соединения. Проверьте интернет и попробуйте ещё раз.',
		failed: (status: number) => `Запрос не выполнен (${status})`,
		internal: 'Что-то пошло не так на нашей стороне. Всё, что вы ввели, сохранено.',
		generic: 'Что-то пошло не так. Пожалуйста, попробуйте ещё раз.',
	},
	fr: {
		noAnswer: 'Le serveur n’a pas répondu. Réessayez dans un instant.',
		aiFailed:
			'L’IA n’a pas pu terminer. Réessayez dans quelques minutes ; un texte plus court passe plus souvent.',
		noConnection: 'Pas de connexion. Vérifiez votre internet et réessayez.',
		failed: (status: number) => `Échec de la requête (${status})`,
		internal: 'Un problème est survenu de notre côté. Rien de ce que vous avez saisi n’est perdu.',
		generic: 'Un problème est survenu. Veuillez réessayer.',
	},
	it: {
		noAnswer: 'Il server non ha risposto. Riprova tra un attimo.',
		aiFailed:
			'L’IA non è riuscita a finire. Riprova tra qualche minuto; con un testo più breve funziona più spesso.',
		noConnection: 'Nessuna connessione. Controlla internet e riprova.',
		failed: (status: number) => `Richiesta non riuscita (${status})`,
		internal:
			'Qualcosa è andato storto da parte nostra. Non hai perso nulla di ciò che hai scritto.',
		generic: 'Qualcosa è andato storto. Riprova.',
	},
	kk: {
		noAnswer: 'Сервер жауап бермеді. Біраздан соң қайталап көріңіз.',
		aiFailed:
			'ЖИ мұны аяқтай алмады. Бірнеше минуттан соң қайталап көріңіз; қысқарақ мәтінмен жиі шығады.',
		noConnection: 'Байланыс жоқ. Интернетті тексеріп, қайталап көріңіз.',
		failed: (status: number) => `Сұраныс орындалмады (${status})`,
		internal: 'Біз жақта бірдеңе дұрыс болмады. Енгізгеніңіздің бәрі сақталды.',
		generic: 'Бірдеңе дұрыс болмады. Қайталап көріңіз.',
	},
	es: {
		noAnswer: 'El servidor no respondió. Inténtalo de nuevo en un momento.',
		aiFailed:
			'La IA no pudo terminar. Inténtalo de nuevo en unos minutos; con un texto más corto funciona más a menudo.',
		noConnection: 'Sin conexión. Revisa tu internet e inténtalo de nuevo.',
		failed: (status: number) => `La solicitud falló (${status})`,
		internal: 'Algo salió mal por nuestra parte. No has perdido nada de lo que escribiste.',
		generic: 'Algo salió mal. Inténtalo de nuevo.',
	},
	pt: {
		noAnswer: 'O servidor não respondeu. Tente de novo em instantes.',
		aiFailed:
			'A IA não conseguiu terminar. Tente de novo em alguns minutos; com um texto mais curto funciona mais vezes.',
		noConnection: 'Sem conexão. Verifique sua internet e tente de novo.',
		failed: (status: number) => `A solicitação falhou (${status})`,
		internal: 'Algo deu errado do nosso lado. Nada do que você digitou foi perdido.',
		generic: 'Algo deu errado. Tente de novo.',
	},
	de: {
		noAnswer: 'Der Server hat nicht geantwortet. Versuch es gleich noch einmal.',
		aiFailed:
			'Die KI konnte das nicht fertigstellen. Versuch es in ein paar Minuten noch einmal; mit einem kürzeren Text klappt es öfter.',
		noConnection: 'Keine Verbindung. Prüf dein Internet und versuch es noch einmal.',
		failed: (status: number) => `Anfrage fehlgeschlagen (${status})`,
		internal:
			'Bei uns ist etwas schiefgelaufen. Nichts von dem, was du eingegeben hast, ist verloren.',
		generic: 'Etwas ist schiefgelaufen. Bitte versuch es noch einmal.',
	},
})

export class ApiError extends Error {
	constructor(
		readonly status: number,
		message: string,
	) {
		super(message)
		this.name = 'ApiError'
	}
}

/**
 * hc resolves on a non-2xx where axios would reject. Every call goes through this, so an
 * `{ error }` body never reaches code expecting the success shape -- `list.map(...)` on an
 * error object throws inside a render and blanks the page.
 */
export const json = async <T>(
	res: HttpResponse,
	/** Statuses the caller handles itself, so they are not reported as problems. */
	expected: number[] = [],
): Promise<T> => {
	if (res.ok) return (await res.json()) as T
	const { message, detail } = await errorText(res)
	// Every refusal she saw, including a 400 for input she got wrong: that is the "did not
	// understand" signal. 401 is not: it is how the app learns there is no session yet.
	if (res.status !== 401 && !expected.includes(res.status)) {
		const where = res.url ? new URL(res.url, location.origin).pathname : '?'
		// A refusal's sentence can quote what she typed back to her (a lesson's own words), and the
		// message goes to the database and to Umami: for a 4xx it is the status and route only. The
		// api's `detail` (the schema fault, a bug of ours) still goes, to the database alone.
		const refusal = res.status >= 400 && res.status < 500
		reportProblem(
			refusal ? `${res.status} ${where}` : `${res.status} ${where}: ${message}`,
			detail,
			{ status: res.status, route: where },
		)
	}
	throw new ApiError(res.status, message)
}

/**
 * What `json` needs from a response, structurally. Not `Response`: the api's own types pull
 * Bun's globals into this compilation, and Bun's `Response` is not hc's `ClientResponse`.
 */
type HttpResponse = {
	ok: boolean
	url?: string
	status: number
	json: () => Promise<unknown>
	text: () => Promise<string>
}

/** The sentence to show, and the api's `detail` for the problem report when it sent one. */
const errorText = async (res: HttpResponse): Promise<{ message: string; detail?: string }> => {
	// A gateway status never carries a message worth showing; the edge wrote it, not the api. The
	// api's own 503 is the model failing (`ai_unavailable`): "the server did not answer" would send
	// her to retry at once what may need a pause or a shorter text.
	if (res.status === 502 || res.status === 503 || res.status === 504) {
		const raw = res.status === 503 ? await res.text().catch(() => '') : ''
		let code: unknown
		try {
			code = (JSON.parse(raw) as { code?: unknown }).code
		} catch {
			code = undefined
		}
		const m = messagesNow(messages)
		return { message: code === 'ai_unavailable' ? m.aiFailed : m.noAnswer }
	}
	const raw = await res.text().catch(() => '')
	if (!raw) return { message: messagesNow(messages).failed(res.status) }
	try {
		const body: unknown = JSON.parse(raw)
		if (body && typeof body === 'object') {
			const { error, message, code, detail } = body as {
				error?: unknown
				message?: unknown
				code?: unknown
				detail?: unknown
			}
			const extra = typeof detail === 'string' ? detail : undefined
			// The api's own word for an unhandled exception. "internal error" tells a teacher
			// nothing and reads like something she broke.
			if (code === 'internal') return { message: messagesNow(messages).internal }
			if (typeof error === 'string') return { message: error, detail: extra }
			if (typeof message === 'string') return { message, detail: extra }
		}
	} catch {
		// Not JSON; the raw text is the best message available.
	}
	// Never let a serialised object reach a teacher. If it is not a sentence, say nothing.
	return {
		message: raw.trimStart().startsWith('{')
			? messagesNow(messages).failed(res.status)
			: raw.slice(0, 300),
	}
}

/** Human-facing copy for a thrown request. Never leaks a stack or a bare status code. */
export const errorMessage = (err: unknown): string => {
	if (err instanceof ApiError) return err.message
	// fetch rejects with a TypeError and the browser's own English ("Failed to fetch", "Load failed",
	// "NetworkError when attempting to fetch resource."). Any other TypeError is a bug, not the network.
	if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message)) {
		return messagesNow(messages).noConnection
	}
	return messagesNow(messages).generic
}
